import { useCallback, useMemo, useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import type { DataTableSelectionMultipleChangeEvent } from 'primereact/datatable';
import { Panel } from 'primereact/panel';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ShipmentStatusTag } from '@/components';
import { ShipmentNumberLink } from '@/components/common/shipments-grid/ShipmentNumberLink';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useRemoveFromShipmentWorkflow } from '@/pages/shipping-details/hooks/useRemoveFromShipmentWorkflow';
import { ShipmentStatus } from '@/pages/shipping/utils/enums';
import { ShipmentDto } from '@/services/shipment-service/types';
import { shipmentEmbryosOptions } from '@/services/shipping';
import type { ShipmentEmbryo } from '@/services/shipping/types';

type EmbryoShipmentDetailsPanelProps = {
  selectedShipment: ShipmentDto | null;
  isShipmentLocked?: boolean;
  onShipmentUpdated?: () => void;
};

export const EmbryoShipmentDetailsPanel = ({
  selectedShipment,
  isShipmentLocked = false,
  onShipmentUpdated,
}: EmbryoShipmentDetailsPanelProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const embryoQueryOptions = shipmentEmbryosOptions(selectedShipment?.id ?? '');

  const { data: shipmentEmbryos = [] } = useQuery(embryoQueryOptions);

  const [isRemoving, setIsRemoving] = useState(false);
  const [selectedEmbryos, setSelectedEmbryos] = useState<ShipmentEmbryo[]>([]);
  const isBulkRemoveEnabled = useFeatureFlag(
    'Embryo_Bulk_Remove_From_Shipment',
  );
  const [lastSelectedShipmentId, setLastSelectedShipmentId] = useState(
    selectedShipment?.id,
  );
  const inFlightRemovalsRef = useRef<ShipmentEmbryo[]>([]);

  if (isBulkRemoveEnabled && selectedShipment?.id !== lastSelectedShipmentId) {
    setLastSelectedShipmentId(selectedShipment?.id);
    setSelectedEmbryos([]);
  }

  const handleWorkflowSuccess = useCallback(() => {
    setIsRemoving(false);
    setSelectedEmbryos([]);
    const removed = inFlightRemovalsRef.current;
    inFlightRemovalsRef.current = [];
    if (selectedShipment?.id) {
      const queryKey = shipmentEmbryosOptions(selectedShipment.id).queryKey;
      if (isBulkRemoveEnabled && removed.length > 0) {
        const removedIds = new Set(removed.map((embryo) => embryo.id));
        queryClient.setQueryData<ShipmentEmbryo[]>(queryKey, (current) =>
          current
            ? current.filter((embryo) => !removedIds.has(embryo.id))
            : current,
        );
      }
      queryClient.invalidateQueries({ queryKey });
    }
    onShipmentUpdated?.();
  }, [queryClient, selectedShipment, onShipmentUpdated, isBulkRemoveEnabled]);

  const handleWorkflowStart = useCallback(() => {
    setIsRemoving(true);
  }, []);

  const handleWorkflowError = useCallback(() => {
    setIsRemoving(false);
    inFlightRemovalsRef.current = [];
  }, []);

  const { executeWorkflow } = useRemoveFromShipmentWorkflow({
    dispatch,
    onStart: isBulkRemoveEnabled ? handleWorkflowStart : undefined,
    onSuccess: handleWorkflowSuccess,
    onError: handleWorkflowError,
  });

  const handleRemoveEmbryo = useCallback(
    (embryo: ShipmentEmbryo) => {
      setIsRemoving(true);
      inFlightRemovalsRef.current = [embryo];
      executeWorkflow([embryo]);
    },
    [executeWorkflow],
  );

  const handleRemoveSelected = useCallback(() => {
    if (selectedEmbryos.length === 0) {
      return;
    }
    inFlightRemovalsRef.current = selectedEmbryos;
    executeWorkflow(selectedEmbryos);
  }, [executeWorkflow, selectedEmbryos]);

  const bulkSelectionProps = isBulkRemoveEnabled
    ? {
        dataKey: 'id',
        selectionMode: 'multiple' as const,
        selection: selectedEmbryos,
        onSelectionChange: (
          event: DataTableSelectionMultipleChangeEvent<ShipmentEmbryo[]>,
        ) => setSelectedEmbryos(event.value),
      }
    : {};

  const headerTemplate = useMemo(() => {
    const isDraft =
      selectedShipment?.shipmentStatus === ShipmentStatus.SCHEDULED;

    return (
      <div className="p-panel-header" data-pc-section="header">
        <div className="flex w-full items-center justify-between">
          <span className="p-panel-title" data-pc-section="title">
            {t('shipping.shipmentDetails')}
          </span>
          {isDraft ? (
            <span className="inline-flex items-center rounded-md bg-orange-500 px-2 py-1 text-xs font-medium text-white">
              {t('shipping.draft')}
            </span>
          ) : null}
        </div>
      </div>
    );
  }, [selectedShipment?.shipmentStatus, t]);

  if (!selectedShipment) {
    return (
      <Panel
        header={t('shipping.shipmentDetails')}
        className="flex h-full flex-col"
      >
        <div className="text-sm text-gray-600">
          {t('shipping.selectShipment')}
        </div>
      </Panel>
    );
  }

  return (
    <>
      <Panel
        headerTemplate={headerTemplate}
        className="flex h-full flex-col overflow-hidden"
      >
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4">
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t('shipping.shipmentNumber')}
                </label>
                <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm">
                  {selectedShipment?.shipmentNumber ? (
                    <ShipmentNumberLink
                      shipmentId={selectedShipment.id}
                      shipmentNumber={selectedShipment.shipmentNumber}
                    />
                  ) : (
                    t('common.na')
                  )}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t('shipping.shipTo')}
                </label>
                <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm">
                  {selectedShipment?.shipToName ??
                    selectedShipment?.shipName ??
                    t('common.na')}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t('shipping.shipmentStatus')}
                </label>
                {selectedShipment?.shipmentStatus ? (
                  <ShipmentStatusTag value={selectedShipment.shipmentStatus} />
                ) : (
                  t('common.na')
                )}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t('common.comments')}
              </label>
              <div className="min-h-[50px] w-full rounded-md border border-gray-300 bg-gray-50 p-2 text-sm">
                {selectedShipment?.comment || t('shipping.noComments')}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t('shipping.trackingNumber')}
              </label>
              <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm">
                {selectedShipment?.trackingNumber || t('common.na')}
              </div>
            </div>
            <div>
              {isBulkRemoveEnabled ? (
                <div className="mb-2 flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('embryo.embryos')}
                  </label>
                  <Tooltip
                    target=".remove-selected-embryos-btn"
                    position="left"
                    content={
                      isShipmentLocked
                        ? t('shipping.cannotModifyLockedShipment')
                        : t('shipping.removeSelectedFromShipment')
                    }
                  />
                  <span className="remove-selected-embryos-btn">
                    <Button
                      label={t('shipping.removeSelected', {
                        total: selectedEmbryos.length,
                      })}
                      icon="pi pi-trash"
                      size="small"
                      severity="danger"
                      outlined
                      disabled={
                        isShipmentLocked ||
                        isRemoving ||
                        selectedEmbryos.length === 0
                      }
                      loading={isRemoving}
                      onClick={handleRemoveSelected}
                    />
                  </span>
                </div>
              ) : (
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  {t('embryo.embryos')}
                </label>
              )}
              <div className="rounded-md border border-gray-200">
                <DataTable
                  value={shipmentEmbryos}
                  emptyMessage={t('shipping.noEmbryosOnShipment')}
                  size="small"
                  className="p-datatable-sm"
                  scrollable
                  scrollHeight="25rem"
                  {...bulkSelectionProps}
                >
                  {isBulkRemoveEnabled ? (
                    <Column
                      selectionMode="multiple"
                      headerStyle={{ width: '3rem' }}
                    />
                  ) : null}
                  <Column field="name" header={t('common.name')} />
                  <Column field="donor.name" header={t('donor.donor')} />
                  <Column field="sire.name" header={t('sire.sireName')} />
                  <Column field="cane" header={t('semen.cane')} />
                  <Column field="stage" header={t('embryo.stage')} />
                  <Column field="grade" header={t('embryo.grade')} />
                  <Column
                    header={t('common.actions')}
                    body={(rowData: ShipmentEmbryo) => (
                      <>
                        <Tooltip
                          target={`.remove-embryo-btn-${rowData.id}`}
                          position="left"
                          content={
                            isShipmentLocked
                              ? t('shipping.cannotModifyLockedShipment')
                              : t('shipping.removeFromShipment')
                          }
                        />
                        <span className={`remove-embryo-btn-${rowData.id}`}>
                          <Button
                            icon="pi pi-trash"
                            size="small"
                            severity="danger"
                            text
                            disabled={isShipmentLocked || isRemoving}
                            loading={isRemoving}
                            onClick={() => handleRemoveEmbryo(rowData)}
                          />
                        </span>
                      </>
                    )}
                    style={{ width: '80px' }}
                  />
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </Panel>
    </>
  );
};
